import { useEffect, useState } from 'react';

/**
 * Server-controlled auto-update hook with rollback support.
 *
 * After login, fetches org_settings (update_channel, min_version, auto_update_enabled)
 * and sends configuration to Electron main process via IPC.
 *
 * On web (non-Electron): no-op.
 * On Electron: configures updater + listens for force-update and rollback events.
 *
 * Rollback: when the server sets `rollbackTo`, the Electron main process detects it
 * and sends `updater:rollback` to the renderer. The UI shows a non-dismissable dialog
 * informing the user that a downgrade is in progress.
 */

interface ForceUpdateState {
  required: boolean;
  requiredVersion: string;
  currentVersion: string;
}

interface RollbackState {
  active: boolean;
  rollbackVersion: string;
  currentVersion: string;
}

// window.zoobook is declared in packages/web/src/lib/auth/biometric.ts (ElectronBridge).
// Do not re-declare here — use (window as unknown as { zoobook?: ElectronBridgeBase })
// to access the shared invoke/on subset without causing duplicate Window augmentation.
interface ElectronBridgeBase {
  invoke: (channel: string, ...args: unknown[]) => Promise<unknown>;
  on: (channel: string, callback: (...args: unknown[]) => void) => () => void;
}

function getElectronBridge(): ElectronBridgeBase | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as { zoobook?: ElectronBridgeBase }).zoobook;
}

export function useAutoUpdate(): ForceUpdateState & { rollback: RollbackState } {
  const [forceUpdate, setForceUpdate] = useState<ForceUpdateState>({
    required: false,
    requiredVersion: '',
    currentVersion: '',
  });

  const [rollback, setRollback] = useState<RollbackState>({
    active: false,
    rollbackVersion: '',
    currentVersion: '',
  });

  // Listen for force-update events from main process (Tier 1 or Tier 2)
  useEffect(() => {
    const bridge = getElectronBridge();
    if (!bridge) return;

    const unsubForce = bridge.on('updater:force-update', (data: unknown) => {
      const { requiredVersion, currentVersion } = data as {
        requiredVersion: string;
        currentVersion: string;
      };
      setForceUpdate({ required: true, requiredVersion, currentVersion });
    });

    const unsubRollback = bridge.on('updater:rollback', (data: unknown) => {
      const { rollbackVersion, currentVersion } = data as {
        rollbackVersion: string;
        currentVersion: string;
      };
      setRollback({ active: true, rollbackVersion, currentVersion });
    });

    return () => {
      unsubForce();
      unsubRollback();
    };
  }, []);

  return { ...forceUpdate, rollback };
}

/**
 * Send org_settings to Electron main process to configure the updater.
 * Call this after login when org_settings are available.
 */
export function configureAutoUpdate(settings: {
  updateChannel: string;
  minVersion: string | null;
  autoUpdateEnabled: boolean;
  betaTester?: boolean;
}): void {
  const bridge = getElectronBridge();
  if (!bridge) return;

  // Per-user beta override: if user opted in, upgrade stable -> beta
  const channel =
    settings.betaTester && settings.updateChannel === 'stable' ? 'beta' : settings.updateChannel;

  bridge
    .invoke('updater:configure', {
      channel,
      minVersion: settings.minVersion,
      autoUpdateEnabled: settings.autoUpdateEnabled,
    })
    .then(() => {
      // Report app version to server for monitoring (organization_members.app_version)
      // TODO: Wire GraphQL mutation when PostGraphile schema is regenerated:
      // mutation { updateOrganizationMember(input: { appVersion, appPlatform, appLastSeen }) }
      return bridge.invoke('app:getVersion');
    })
    .catch((err: unknown) => {
      console.error('[useAutoUpdate] Failed to configure updater:', err);
    });
}
